import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Visibility,
  CheckCircle,
  HighlightOff,
  FilterList,
  Refresh,
  LocalShipping,
} from '@mui/icons-material';
import {
  PageHeader,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatCurrency } from '../../utils/currency';
import scmService, {
  GoodsReceipt,
  GoodsReceiptDetail,
  GoodsReceiptItem,
  GrnStatus,
  GRN_STATUSES,
  PurchaseOrderOption,
  UpdateGrnStatusItemInput,
} from '../../services/scmService';
import { procurementService, Vendor } from '../../services/procurementService';
import GoodsReceiptFormDialog from './GoodsReceiptFormPage';

const statusTone: Record<GrnStatus, StatusTone> = {
  DRAFT: 'neutral',
  RECEIVED: 'info',
  QC_PENDING: 'warning',
  ACCEPTED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const EPSILON = 0.005;

// ---------------------------------------------------------------------------
// QC / detail dialog
// ---------------------------------------------------------------------------

interface QcDraft {
  accepted: string;
  rejected: string;
  reason: string;
}

const GoodsReceiptDetailDialog: React.FC<{
  grnId: number | null;
  onClose: () => void;
  onChanged: () => void;
}> = ({ grnId, onClose, onChanged }) => {
  const [grn, setGrn] = useState<GoodsReceiptDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qc, setQc] = useState<Record<number, QcDraft>>({});
  const [saving, setSaving] = useState<'QC_PENDING' | 'ACCEPTED' | 'REJECTED' | 'CANCEL' | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = useCallback(() => {
    if (!grnId) return;
    setLoading(true);
    setError(null);
    scmService
      .getGoodsReceiptById(grnId)
      .then(({ goodsReceipt }) => {
        setGrn(goodsReceipt);
        const draft: Record<number, QcDraft> = {};
        goodsReceipt.items.forEach((item) => {
          const received = toNum(item.received_quantity);
          const acceptedSoFar = toNum(item.accepted_quantity);
          const rejectedSoFar = toNum(item.rejected_quantity);
          draft[item.id] = {
            accepted: String(acceptedSoFar || received),
            rejected: String(rejectedSoFar || 0),
            reason: item.rejection_reason || '',
          };
        });
        setQc(draft);
      })
      .catch((err: any) => setError(err.message || 'Failed to load goods receipt'))
      .finally(() => setLoading(false));
  }, [grnId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateQc = (itemId: number, patch: Partial<QcDraft>) => {
    setQc((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  };

  const qcLineInvalid = useCallback(
    (item: GoodsReceiptItem): boolean => {
      const draft = qc[item.id];
      if (!draft) return false;
      const accepted = toNum(draft.accepted);
      const rejected = toNum(draft.rejected);
      const received = toNum(item.received_quantity);
      return accepted < 0 || rejected < 0 || accepted + rejected - received > EPSILON;
    },
    [qc]
  );

  const qcInvalid = useMemo(() => grn?.items.some(qcLineInvalid) ?? false, [grn, qcLineInvalid]);

  const advanceToQc = async () => {
    if (!grn) return;
    setSaving('QC_PENDING');
    setError(null);
    try {
      await scmService.updateGoodsReceiptStatus(grn.id, { status: 'QC_PENDING' });
      onChanged();
      load();
    } catch (err: any) {
      setError(err.message || 'Failed to move to QC');
    } finally {
      setSaving(null);
    }
  };

  const completeQc = async (target: 'ACCEPTED' | 'REJECTED') => {
    if (!grn) return;
    setSaving(target);
    setError(null);
    try {
      const items: UpdateGrnStatusItemInput[] = grn.items.map((item) => {
        const draft = qc[item.id];
        return {
          id: item.id,
          acceptedQuantity: toNum(draft?.accepted),
          rejectedQuantity: toNum(draft?.rejected),
          rejectionReason: draft?.reason.trim() || null,
        };
      });
      await scmService.updateGoodsReceiptStatus(grn.id, { status: target, items });
      onChanged();
      load();
    } catch (err: any) {
      setError(err.message || 'Failed to complete quality check');
    } finally {
      setSaving(null);
    }
  };

  const doCancel = async () => {
    if (!grn) return;
    setSaving('CANCEL');
    setError(null);
    try {
      await scmService.cancelGoodsReceipt(grn.id);
      setConfirmCancel(false);
      onChanged();
      load();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel goods receipt');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={!!grnId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalShipping fontSize="small" color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
              {grn?.grn_number || 'Goods Receipt'}
            </Typography>
            {grn && <StatusDot label={grn.status} tone={statusTone[grn.status]} />}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !grn ? (
          <Alert severity="error">{error || 'Not found'}</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">PO</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{grn.po_number}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Vendor</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{grn.vendor_name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Warehouse</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{grn.warehouse_name || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Receipt Date</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {new Date(grn.receipt_date).toLocaleDateString('en-IN')}
                </Typography>
              </Box>
              {grn.vendor_dc_number && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Vendor DC</Typography>
                  <Typography variant="body2">{grn.vendor_dc_number}</Typography>
                </Box>
              )}
              {grn.vehicle_number && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Vehicle</Typography>
                  <Typography variant="body2">{grn.vehicle_number}</Typography>
                </Box>
              )}
              {grn.eway_bill_number && (
                <Box>
                  <Typography variant="caption" color="text.secondary">E-Way Bill</Typography>
                  <Typography variant="body2">{grn.eway_bill_number}</Typography>
                </Box>
              )}
            </Box>

            {grn.status === 'RECEIVED' && (
              <Alert severity="info">
                This receipt is logged and stock has already moved. Move it into quality check to record accepted /
                rejected quantities.
              </Alert>
            )}

            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Product</TableCell>
                    <TableCell sx={headerCellSx} align="right">Ordered</TableCell>
                    <TableCell sx={headerCellSx} align="right">Received</TableCell>
                    <TableCell sx={headerCellSx} align="right">Unit Price</TableCell>
                    {grn.status === 'QC_PENDING' ? (
                      <>
                        <TableCell sx={headerCellSx}>Accepted</TableCell>
                        <TableCell sx={headerCellSx}>Rejected</TableCell>
                        <TableCell sx={headerCellSx}>Rejection Reason</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={headerCellSx} align="right">Accepted</TableCell>
                        <TableCell sx={headerCellSx} align="right">Rejected</TableCell>
                        <TableCell sx={headerCellSx}>Reason</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grn.items.map((item) => {
                    const invalid = grn.status === 'QC_PENDING' && qcLineInvalid(item);
                    const draft = qc[item.id];
                    return (
                      <TableRow key={item.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.product_name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.product_code} {item.unit_of_measure ? `· ${item.unit_of_measure}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.ordered_quantity)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.received_quantity)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatCurrency(toNum(item.unit_price))}
                        </TableCell>
                        {grn.status === 'QC_PENDING' ? (
                          <>
                            <TableCell sx={{ minWidth: 100 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={draft?.accepted ?? ''}
                                error={invalid}
                                onChange={(e) => updateQc(item.id, { accepted: e.target.value })}
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{ minWidth: 90 }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={draft?.rejected ?? ''}
                                error={invalid}
                                onChange={(e) => updateQc(item.id, { rejected: e.target.value })}
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{ minWidth: 90 }}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              <TextField
                                size="small"
                                value={draft?.reason ?? ''}
                                onChange={(e) => updateQc(item.id, { reason: e.target.value })}
                                placeholder="Required if rejecting"
                                sx={{ minWidth: 150 }}
                              />
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {toNum(item.accepted_quantity)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {toNum(item.rejected_quantity)}
                            </TableCell>
                            <TableCell>{item.rejection_reason || '—'}</TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {grn.status === 'QC_PENDING' && qcInvalid && (
              <Alert severity="warning">
                For every line, accepted + rejected quantity cannot exceed the received quantity.
              </Alert>
            )}

            {grn.remarks && (
              <Typography variant="body2" color="text.secondary">
                <strong>Remarks:</strong> {grn.remarks}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, justifyContent: 'space-between' }}>
        <Box>
          {grn && grn.status !== 'CANCELLED' && (
            <Button color="error" onClick={() => setConfirmCancel(true)} disabled={!!saving}>
              Cancel Receipt
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Close</Button>
          {grn?.status === 'RECEIVED' && (
            <Button variant="contained" onClick={advanceToQc} disabled={saving === 'QC_PENDING'}>
              {saving === 'QC_PENDING' ? 'Moving…' : 'Move to Quality Check'}
            </Button>
          )}
          {grn?.status === 'QC_PENDING' && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<HighlightOff />}
                onClick={() => completeQc('REJECTED')}
                disabled={qcInvalid || saving === 'REJECTED'}
              >
                {saving === 'REJECTED' ? 'Saving…' : 'Mark Rejected'}
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => completeQc('ACCEPTED')}
                disabled={qcInvalid || saving === 'ACCEPTED'}
              >
                {saving === 'ACCEPTED' ? 'Saving…' : 'Mark Accepted'}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>

      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
        <DialogTitle>Cancel Goods Receipt</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This reverses the stock this receipt added and rolls back the purchase order's received quantity,
            regardless of its current quality-check status. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(false)} disabled={saving === 'CANCEL'}>
            Keep It
          </Button>
          <Button color="error" variant="contained" onClick={doCancel} disabled={saving === 'CANCEL'}>
            {saving === 'CANCEL' ? 'Cancelling…' : 'Cancel & Reverse Stock'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// List page
// ---------------------------------------------------------------------------

const GoodsReceiptPage: React.FC = () => {
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [pos, setPos] = useState<PurchaseOrderOption[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);

  const [poFilter, setPoFilter] = useState<PurchaseOrderOption | null>(null);
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<GrnStatus | ''>('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');

  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; grn: GoodsReceipt } | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchReceipts = useCallback(() => {
    setLoading(true);
    setError(null);
    scmService
      .getGoodsReceipts({
        poId: poFilter?.id,
        vendorId: vendorFilter || undefined,
        status: statusFilter || undefined,
        from: fromFilter || undefined,
        to: toFilter || undefined,
      })
      .then(({ goodsReceipts }) => setReceipts(goodsReceipts))
      .catch((err: any) => setError(err.message || 'Failed to load goods receipts'))
      .finally(() => setLoading(false));
  }, [poFilter, vendorFilter, statusFilter, fromFilter, toFilter]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  useEffect(() => {
    scmService.listPurchaseOrders().then((r) => setPos(r.purchaseOrders)).catch(() => {});
    procurementService.getVendors().then((r) => setVendors(r.vendors)).catch(() => {});
  }, []);

  const clearFilters = () => {
    setPoFilter(null);
    setVendorFilter('');
    setStatusFilter('');
    setFromFilter('');
    setToFilter('');
  };

  const hasFilters = !!(poFilter || vendorFilter || statusFilter || fromFilter || toFilter);

  return (
    <Box>
      <PageHeader
        title="Goods Receipts"
        subtitle="Receive stock against a purchase order and run it through quality check"
        actions={
          <>
            <IconButton onClick={fetchReceipts} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => setFormOpen(true)}>
              New Goods Receipt
            </Button>
          </>
        }
      />

      <FilterBar>
        <Autocomplete
          options={pos}
          size="small"
          sx={{ minWidth: 220 }}
          getOptionLabel={(o) => o.po_number}
          value={poFilter}
          onChange={(_, v) => setPoFilter(v)}
          renderInput={(params) => <TextField {...params} label="Purchase Order" />}
        />

        <TextField
          select
          size="small"
          label="Vendor"
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All Vendors</MenuItem>
          {vendors.map((v) => (
            <MenuItem key={v.id} value={v.id}>
              {v.company_name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as GrnStatus | '')}
          sx={{ minWidth: 160 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {GRN_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace('_', ' ')}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          type="date"
          label="From"
          value={fromFilter}
          onChange={(e) => setFromFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={toFilter}
          onChange={(e) => setToFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['GRN', 'PO', 'Vendor', 'Warehouse', 'Receipt Date', 'Status', ''].map((h, i) => (
                  <TableCell key={h || i} align={i === 6 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : receipts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No goods receipts match these filters"
                      hint="Clear the filters, or receive goods against a PO."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                receipts.map((grn) => (
                  <TableRow key={grn.id} sx={bodyRowSx} hover>
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={grn.grn_number} tint="#0284c7" icon={<LocalShipping fontSize="small" />} />
                    </TableCell>
                    <TableCell>{grn.po_number}</TableCell>
                    <TableCell>{grn.vendor_name}</TableCell>
                    <TableCell>{grn.warehouse_name || '—'}</TableCell>
                    <TableCell>{new Date(grn.receipt_date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      <StatusDot label={grn.status.replace('_', ' ')} tone={statusTone[grn.status]} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${grn.grn_number}`}
                        onClick={(e) => setRowMenu({ anchor: e.currentTarget, grn })}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      <Menu anchorEl={rowMenu?.anchor || null} open={!!rowMenu} onClose={() => setRowMenu(null)}>
        <MenuItem
          onClick={() => {
            if (rowMenu) setDetailId(rowMenu.grn.id);
            setRowMenu(null);
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View / Quality Check</ListItemText>
        </MenuItem>
      </Menu>

      <GoodsReceiptFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={() => {
          setToast({ message: 'Goods receipt created', severity: 'success' });
          fetchReceipts();
        }}
      />

      <GoodsReceiptDetailDialog
        grnId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={() => {
          fetchReceipts();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default GoodsReceiptPage;
